import React, { useEffect, useMemo, useState } from "react";
import AppLayout from "@/components/AppLayout";
import { isSupervisorOrAbove, isAdminOrAbove } from "@/lib/permissions";
import api, { API_BASE } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "sonner";
import { Plus, DownloadSimple } from "@phosphor-icons/react";

const DAYS_OF_WEEK = ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"];
const TRUCK_UNITS = ["BSVAC-2", "BSVAC-4", "BSVAC-5", "CBVAC-6"];

/** Today's YYYY-MM-DD in Eastern Time — Supervisor Hub is anchored to ET
 *  everywhere (matches Schedule + Triage). */
function etTodayYMD() {
  return new Intl.DateTimeFormat("en-CA", {
    timeZone: "America/New_York",
    year: "numeric", month: "2-digit", day: "2-digit",
  }).format(new Date());
}
/** Weekday name for a YYYY-MM-DD string, resolved on the ET calendar. */
function dowForYMD(ymd) {
  if (!ymd) return "";
  // Anchor at 12:00 UTC on that date so DST edges never flip the ET day.
  const [y, m, d] = ymd.split("-").map(Number);
  const dt = new Date(Date.UTC(y, m - 1, d, 12));
  return dt.toLocaleDateString("en-US", { weekday: "long", timeZone: "America/New_York" });
}

import Reports from "@/pages/Reports";
import { DropToursTab } from "@/pages/AdminReports";

export default function Supervisor() {
  const { user: currentUser } = useAuth();
  const [tab, setTab] = useState("tour");
  const [users, setUsers] = useState([]);
  const [tourReports, setTourReports] = useState([]);
  const [incidents, setIncidents] = useState([]);
  const [writeups, setWriteups] = useState([]);

  async function load() {
    const [u, t, i, w] = await Promise.all([
      api.get("/users/"),
      api.get("/supervisor/tour-reports"),
      api.get("/supervisor/incidents"),
      api.get("/supervisor/writeups"),
    ]);
    setUsers(u.data);
    setTourReports(t.data);
    setIncidents(i.data);
    setWriteups(w.data);
  }
  useEffect(() => { load(); }, []);

  const supervisors = users.filter((u) => (isSupervisorOrAbove(u) || isAdminOrAbove(u)) && u.active !== false);

  return (
    <AppLayout title="Supervisor Hub" subtitle="Daily tour reports, radio trend, hour reports, drop patterns, and disciplinary write-ups">
      <Tabs value={tab} onValueChange={setTab} className="w-full">
        <TabsList className="rounded-sm bg-slate-100 mb-4 flex-wrap h-auto">
          <TabsTrigger value="tour" data-testid="tab-tour" className="rounded-sm">Daily Tour Reports</TabsTrigger>
          <TabsTrigger value="radio" data-testid="tab-radio" className="rounded-sm">Radio Trend</TabsTrigger>
          <TabsTrigger value="hours" data-testid="tab-hours" className="rounded-sm">Hour Reports</TabsTrigger>
          <TabsTrigger value="drops" data-testid="tab-drops" className="rounded-sm">Tour Drop Pattern Watch</TabsTrigger>
          <TabsTrigger value="incident" data-testid="tab-incident" className="rounded-sm">Incident Reports</TabsTrigger>
          <TabsTrigger value="writeup" data-testid="tab-writeup" className="rounded-sm">Disciplinary Write-Ups</TabsTrigger>
        </TabsList>

        <TabsContent value="tour"><TourReportsTab rows={tourReports} supervisors={supervisors} currentUser={currentUser} reload={load} /></TabsContent>
        <TabsContent value="radio"><RadioTrendTab rows={tourReports} /></TabsContent>
        <TabsContent value="hours"><Reports embedded /></TabsContent>
        <TabsContent value="drops"><DropToursTab /></TabsContent>
        <TabsContent value="incident"><IncidentTab rows={incidents} users={users} reload={load} /></TabsContent>
        <TabsContent value="writeup"><WriteUpTab rows={writeups} users={users} reload={load} /></TabsContent>
      </Tabs>
    </AppLayout>
  );
}

function NewButton({ children, ...rest }) {
  return <Button className="rounded-sm bg-slate-900 text-white" {...rest}><Plus size={14} weight="bold" className="mr-1" />{children}</Button>;
}

/* ===================== Daily Tour Reports ===================== */
const EMPTY_TOUR = {
  tour_date: "",
  supervisor_user_id: "",
  day_of_week: "",
  // Structured inventory check (per BSVAC end-of-tour checklist).
  pd_radios_qty: "",
  bsvac_radios_qty: "",
  truck_checked: "",
  base_clean: "",
  trucks_at_base: [],
  // End-of-tour paperwork check.
  paperwork_check: "",
  paperwork_notes: "",
  notes: "",
};

function TourReportsTab({ rows, supervisors, currentUser, reload }) {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY_TOUR);

  // Every time the dialog is opened, prefill:
  //  · date  → today (Eastern Time)
  //  · day   → derived from that date on the ET calendar
  //  · supervisor → the currently signed-in supervisor
  // The user can still override anything before saving.
  function reset() { setForm(EMPTY_TOUR); }
  function seedForOpen() {
    const today = etTodayYMD();
    const meIsSupervisor = supervisors.some((s) => s.id === currentUser?.id);
    setForm({
      ...EMPTY_TOUR,
      tour_date: today,
      day_of_week: dowForYMD(today),
      supervisor_user_id: meIsSupervisor ? currentUser.id : "",
    });
  }

  // If the operator changes the date, keep day-of-week locked to that date.
  function onDate(value) {
    setForm((f) => ({ ...f, tour_date: value, day_of_week: dowForYMD(value) }));
  }

  function toggleTruck(unit) {
    setForm((f) => ({
      ...f,
      trucks_at_base: f.trucks_at_base.includes(unit)
        ? f.trucks_at_base.filter((x) => x !== unit)
        : [...f.trucks_at_base, unit],
    }));
  }

  async function save(e) {
    e.preventDefault();
    if (!form.tour_date) { toast.error("Date is required"); return; }
    if (!form.supervisor_user_id) { toast.error("Supervisor on Duty is required"); return; }
    const sup = supervisors.find((s) => s.id === form.supervisor_user_id);
    // Build a human-readable summary of the structured checklist so the
    // list / PDF continues to show something meaningful even for consumers
    // that only know the legacy `inventory_check` field.
    const summary = [
      form.pd_radios_qty !== "" ? `PD Radios: ${form.pd_radios_qty}` : null,
      form.bsvac_radios_qty !== "" ? `BSVAC Radios: ${form.bsvac_radios_qty}` : null,
      form.truck_checked ? `Truck Checked: ${form.truck_checked.toUpperCase()}` : null,
      form.base_clean ? `Base Clean & Organized: ${form.base_clean.toUpperCase()}` : null,
      form.trucks_at_base.length ? `Trucks at Base: ${form.trucks_at_base.join(", ")}` : null,
      form.paperwork_check ? `Paperwork Completed: ${form.paperwork_check.toUpperCase()}` : null,
    ].filter(Boolean).join(" · ");
    const payload = {
      ...form,
      pd_radios_qty: form.pd_radios_qty === "" ? null : Number(form.pd_radios_qty),
      bsvac_radios_qty: form.bsvac_radios_qty === "" ? null : Number(form.bsvac_radios_qty),
      truck_checked: form.truck_checked || null,
      base_clean: form.base_clean || null,
      paperwork_check: form.paperwork_check || null,
      paperwork_notes: form.paperwork_notes || null,
      inventory_check: summary || null,
      supervisor_name: sup?.name || null,
    };
    try {
      await api.post("/supervisor/tour-reports", payload);
      toast.success("Tour report saved");
      setOpen(false);
      reset();
      reload();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed");
    }
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <p className="text-xs text-slate-500">Daily operational tour log — date, on-duty supervisor, inventory check, and notes.</p>
        <Dialog
          open={open}
          onOpenChange={(o) => {
            setOpen(o);
            if (o) seedForOpen();
            else reset();
          }}
        >
          <DialogTrigger asChild><NewButton data-testid="tour-new">New Tour Report</NewButton></DialogTrigger>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader className="pb-2 border-b border-slate-200">
              <DialogTitle className="font-display text-xl">Daily Tour Report</DialogTitle>
              <p className="text-xs text-slate-500 mt-1">Bedford Stuyvesant Volunteer Ambulance Corps</p>
            </DialogHeader>
            <form onSubmit={save} className="space-y-5 pt-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-1.5">
                  <Label className="tab-tag">Date</Label>
                  <Input type="date" required value={form.tour_date} onChange={(e) => onDate(e.target.value)} className="rounded-sm" data-testid="tour-date" />
                  <div className="text-[10px] text-slate-500">Defaults to today · Eastern Time</div>
                </div>
                <div className="space-y-1.5">
                  <Label className="tab-tag">Day of the Week</Label>
                  <Input readOnly value={form.day_of_week} className="rounded-sm bg-slate-50 text-slate-700" data-testid="tour-dow" />
                  <div className="text-[10px] text-slate-500">Auto-filled from the date</div>
                </div>
              </div>

              <div className="space-y-1.5">
                <Label className="tab-tag">Supervisor on Duty</Label>
                <select
                  required
                  value={form.supervisor_user_id}
                  onChange={(e) => setForm({ ...form, supervisor_user_id: e.target.value })}
                  className="w-full border border-slate-200 rounded-sm h-10 px-2 text-sm bg-white"
                  data-testid="tour-supervisor"
                >
                  <option value="">Select supervisor…</option>
                  {supervisors.map((s) => (
                    <option key={s.id} value={s.id}>
                      {s.name} · {s.role}{s.id === currentUser?.id ? " (you)" : ""}
                    </option>
                  ))}
                </select>
                <div className="text-[10px] text-slate-500">Defaults to the signed-in supervisor</div>
              </div>

              <div className="border border-slate-200 rounded-sm p-3 bg-slate-50 space-y-4">
                <div>
                  <Label className="tab-tag">Inventory Check</Label>
                  <p className="text-[10px] text-slate-500">End-of-tour equipment and base checklist.</p>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-1.5">
                    <Label className="tab-tag">PD Radios (qty)</Label>
                    <Input
                      type="number"
                      min="0"
                      inputMode="numeric"
                      className="rounded-sm bg-white"
                      value={form.pd_radios_qty}
                      onChange={(e) => setForm({ ...form, pd_radios_qty: e.target.value })}
                      placeholder="0"
                      data-testid="tour-pd-radios"
                    />
                  </div>
                  <div className="space-y-1.5">
                    <Label className="tab-tag">BSVAC Radios (qty)</Label>
                    <Input
                      type="number"
                      min="0"
                      inputMode="numeric"
                      className="rounded-sm bg-white"
                      value={form.bsvac_radios_qty}
                      onChange={(e) => setForm({ ...form, bsvac_radios_qty: e.target.value })}
                      placeholder="0"
                      data-testid="tour-bsvac-radios"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-1.5">
                    <Label className="tab-tag">Truck Checked</Label>
                    <select
                      value={form.truck_checked}
                      onChange={(e) => setForm({ ...form, truck_checked: e.target.value })}
                      className="w-full border border-slate-200 rounded-sm h-10 px-2 text-sm bg-white"
                      data-testid="tour-truck-checked"
                    >
                      <option value="">Select…</option>
                      <option value="yes">Yes</option>
                      <option value="no">No</option>
                    </select>
                  </div>
                  <div className="space-y-1.5">
                    <Label className="tab-tag">Base Clean &amp; Organized</Label>
                    <select
                      value={form.base_clean}
                      onChange={(e) => setForm({ ...form, base_clean: e.target.value })}
                      className="w-full border border-slate-200 rounded-sm h-10 px-2 text-sm bg-white"
                      data-testid="tour-base-clean"
                    >
                      <option value="">Select…</option>
                      <option value="yes">Yes</option>
                      <option value="no">No</option>
                    </select>
                  </div>
                </div>

                <div className="space-y-1.5">
                  <Label className="tab-tag">Trucks at Base</Label>
                  <p className="text-[10px] text-slate-500">Check every unit that is currently parked at base.</p>
                  <div className="grid grid-cols-2 gap-2">
                    {TRUCK_UNITS.map((u) => (
                      <label
                        key={u}
                        className="flex items-center gap-2 text-sm bg-white border border-slate-200 rounded-sm px-2 py-1.5 hover:border-slate-400 cursor-pointer"
                        data-testid={`tour-truck-${u}`}
                      >
                        <Checkbox
                          checked={form.trucks_at_base.includes(u)}
                          onCheckedChange={() => toggleTruck(u)}
                        />
                        <span className="mono text-xs">{u}</span>
                      </label>
                    ))}
                  </div>
                </div>
              </div>

              {/* ── Paperwork Check ────────────────────────────────
                  Separate card so it reads independently from the
                  inventory checklist and prints as its own PDF section. */}
              <div className="border border-slate-200 rounded-sm p-3 bg-slate-50 space-y-4" data-testid="tour-paperwork-card">
                <div>
                  <Label className="tab-tag">Paperwork Check</Label>
                  <p className="text-[10px] text-slate-500">Confirm all end-of-tour paperwork (ACRs, incident/OTC forms, reports) has been completed.</p>
                </div>
                <div className="space-y-1.5">
                  <Label className="tab-tag">All Tour Paperwork Completed</Label>
                  <div className="flex gap-2">
                    {["yes", "no"].map((v) => (
                      <button
                        key={v}
                        type="button"
                        onClick={() => setForm({ ...form, paperwork_check: v })}
                        className={`flex-1 rounded-sm border px-3 py-2 text-xs font-semibold uppercase tracking-wide ${
                          form.paperwork_check === v
                            ? v === "yes"
                              ? "bg-emerald-600 text-white border-emerald-700"
                              : "bg-red-600 text-white border-red-700"
                            : "bg-white text-slate-700 border-slate-300 hover:border-slate-500"
                        }`}
                        data-testid={`tour-paperwork-${v}`}
                      >
                        {v}
                      </button>
                    ))}
                  </div>
                </div>
                {form.paperwork_check === "no" && (
                  <div className="space-y-1.5">
                    <Label className="tab-tag text-red-700">What's still outstanding?</Label>
                    <Textarea
                      className="rounded-sm bg-white min-h-[70px]"
                      placeholder="List any missing ACRs / OTCs / reports…"
                      value={form.paperwork_notes}
                      onChange={(e) => setForm({ ...form, paperwork_notes: e.target.value })}
                      data-testid="tour-paperwork-notes"
                    />
                  </div>
                )}
              </div>

              <div className="space-y-1.5">
                <Label className="tab-tag">Notes</Label>
                <Textarea
                  className="rounded-sm min-h-[120px] resize-y"
                  placeholder="Tour observations, crew performance, follow-up items…"
                  value={form.notes}
                  onChange={(e) => setForm({ ...form, notes: e.target.value })}
                  data-testid="tour-notes"
                />
              </div>

              <DialogFooter className="pt-3 border-t border-slate-200 gap-2">
                <Button type="button" variant="outline" className="rounded-sm" onClick={() => { setOpen(false); reset(); }} data-testid="tour-cancel">Cancel</Button>
                <Button type="submit" className="rounded-sm bg-slate-900 text-white" data-testid="tour-save">Save Report</Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      <div className="surface-grid">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 border-b border-slate-200">
            <tr className="text-left tab-tag text-slate-500">
              <th className="px-4 py-2">Date</th>
              <th className="px-4 py-2">Day</th>
              <th className="px-4 py-2">Supervisor</th>
              <th className="px-4 py-2">Filed By</th>
              <th className="px-4 py-2 text-right">PDF</th>
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 ? (
              <tr><td colSpan={5} className="px-4 py-6 text-slate-500">No reports yet.</td></tr>
            ) : rows.map((r) => (
              <tr key={r.id} className="border-b border-slate-100 hover:bg-slate-50" data-testid={`tour-row-${r.id}`}>
                <td className="px-4 py-2 mono">{r.tour_date}</td>
                <td className="px-4 py-2"><Badge className="rounded-sm bg-slate-700 text-white text-[10px]">{r.day_of_week || "—"}</Badge></td>
                <td className="px-4 py-2">{r.supervisor_name || "—"}</td>
                <td className="px-4 py-2">{r.author_name}</td>
                <td className="px-4 py-2 text-right">
                  <a className="text-xs underline" href={`${API_BASE}/supervisor/tour-reports/${r.id}/pdf`} target="_blank" rel="noreferrer">
                    <DownloadSimple size={12} className="inline mr-1" />PDF
                  </a>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

/* ===================== Radio Trend ===================== */
/** Line-chart + table view over the last N tour reports so a supervisor can
 *  spot when the reported radio count drops from one tour to the next. The
 *  entry immediately before the current row is used as the baseline for
 *  the discrepancy flag — matching the "spot a missing radio by the next
 *  tour" ask from the client. */
function RadioTrendTab({ rows }) {
  // Filter to reports that actually declared a radio count, then order by
  // date ASC so `prev` in the map is the immediately-preceding tour.
  const trend = useMemo(() => {
    const withData = (rows || []).filter((r) =>
      r.pd_radios_qty !== null && r.pd_radios_qty !== undefined ||
      r.bsvac_radios_qty !== null && r.bsvac_radios_qty !== undefined
    );
    const asc = [...withData].sort((a, b) => String(a.tour_date).localeCompare(String(b.tour_date)));
    let prev = null;
    const enriched = asc.map((r) => {
      const pd = r.pd_radios_qty ?? null;
      const bs = r.bsvac_radios_qty ?? null;
      const dPd = prev && prev.pd != null && pd != null ? pd - prev.pd : null;
      const dBs = prev && prev.bs != null && bs != null ? bs - prev.bs : null;
      const row = {
        id: r.id,
        date: r.tour_date,
        supervisor: r.supervisor_name || "—",
        pd, bs, dPd, dBs,
        // A "discrepancy" is any negative delta — the count dropped.
        pdMissing: dPd != null && dPd < 0 ? Math.abs(dPd) : 0,
        bsMissing: dBs != null && dBs < 0 ? Math.abs(dBs) : 0,
      };
      prev = { pd, bs };
      return row;
    });
    // Display newest-first — but retain the delta computed against the
    // chronologically previous tour, not the row above.
    return enriched.reverse();
  }, [rows]);

  const flags = trend.filter((t) => t.pdMissing || t.bsMissing);

  // Simple SVG line chart (no chart library needed). Uses the ASC-sorted
  // series so trends run left → right chronologically.
  const chart = useMemo(() => {
    const asc = [...trend].reverse();
    if (asc.length === 0) return null;
    const W = 720, H = 220, P = 32;
    const xs = asc.map((_, i) => P + (i * (W - 2 * P)) / Math.max(1, asc.length - 1));
    const maxY = Math.max(1, ...asc.map((r) => Math.max(r.pd || 0, r.bs || 0))) + 1;
    const y = (v) => H - P - ((v || 0) / maxY) * (H - 2 * P);
    const line = (key) => asc.map((r, i) => `${i === 0 ? "M" : "L"}${xs[i]},${y(r[key])}`).join(" ");
    const dots = (key, color, testid) => asc.map((r, i) => (
      <circle key={`${key}-${i}`} cx={xs[i]} cy={y(r[key])} r="3.5" fill={color} data-testid={testid && `${testid}-${i}`} />
    ));
    return (
      <svg viewBox={`0 0 ${W} ${H}`} className="w-full h-56 bg-slate-50 border border-slate-200 rounded-sm">
        {/* horizontal gridlines */}
        {[0, 0.25, 0.5, 0.75, 1].map((f, i) => (
          <line key={i} x1={P} x2={W - P} y1={H - P - f * (H - 2 * P)} y2={H - P - f * (H - 2 * P)} stroke="#e2e8f0" strokeDasharray="3 3" />
        ))}
        <path d={line("pd")} fill="none" stroke="#0f172a" strokeWidth="2" />
        <path d={line("bs")} fill="none" stroke="#2563eb" strokeWidth="2" />
        {dots("pd", "#0f172a", "chart-pd")}
        {dots("bs", "#2563eb", "chart-bs")}
        {/* x-axis date labels — every Nth to avoid overlap */}
        {asc.map((r, i) => (i % Math.max(1, Math.ceil(asc.length / 8)) === 0) && (
          <text key={`lbl-${i}`} x={xs[i]} y={H - 8} fontSize="10" textAnchor="middle" fill="#64748b">{r.date.slice(5)}</text>
        ))}
        {/* legend */}
        <g transform={`translate(${W - 180}, 12)`}>
          <rect width="170" height="34" fill="white" stroke="#cbd5e1" />
          <circle cx="14" cy="12" r="4" fill="#0f172a" />
          <text x="24" y="15" fontSize="10" fill="#334155">PD Radios</text>
          <circle cx="14" cy="26" r="4" fill="#2563eb" />
          <text x="24" y="29" fontSize="10" fill="#334155">BSVAC Radios</text>
        </g>
      </svg>
    );
  }, [trend]);

  return (
    <div className="space-y-4">
      <p className="text-xs text-slate-500">
        Radio counts across consecutive Daily Tour Reports. When today's count is lower than the immediately-previous tour, the row is flagged.
      </p>

      {flags.length > 0 && (
        <div className="border-l-4 border-red-600 bg-red-50 px-3 py-2 text-sm text-red-900" data-testid="radio-flag-banner">
          <span className="font-bold uppercase tracking-wider text-[11px]">⚠ Radio Count Discrepancy</span>
          <div className="mt-1 space-y-0.5">
            {flags.map((f) => (
              <div key={f.id} className="mono text-xs">
                {f.date}: {f.bsMissing ? `${f.bsMissing} BSVAC radio${f.bsMissing === 1 ? "" : "s"} unaccounted for` : ""}
                {f.bsMissing && f.pdMissing ? " · " : ""}
                {f.pdMissing ? `${f.pdMissing} PD radio${f.pdMissing === 1 ? "" : "s"} unaccounted for` : ""}
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="surface-grid p-3">
        {chart || <div className="text-xs text-slate-500 p-6 text-center">No radio counts have been recorded in any Tour Report yet.</div>}
      </div>

      <div className="surface-grid">
        <table className="w-full text-sm">
          <thead className="text-[10px] uppercase tracking-widest text-slate-500 border-b border-slate-200">
            <tr>
              <th className="px-4 py-2 text-left">Date</th>
              <th className="px-4 py-2 text-left">Supervisor</th>
              <th className="px-4 py-2 text-right">PD Radios</th>
              <th className="px-4 py-2 text-right">Δ PD</th>
              <th className="px-4 py-2 text-right">BSVAC Radios</th>
              <th className="px-4 py-2 text-right">Δ BSVAC</th>
              <th className="px-4 py-2 text-left">Flag</th>
            </tr>
          </thead>
          <tbody>
            {trend.length === 0 ? (
              <tr><td colSpan={7} className="px-4 py-6 text-center text-slate-500">No radio counts recorded yet.</td></tr>
            ) : trend.map((r) => (
              <tr key={r.id} className={`border-b border-slate-100 ${r.pdMissing || r.bsMissing ? "bg-red-50" : ""}`} data-testid={`radio-row-${r.id}`}>
                <td className="px-4 py-2 mono text-xs">{r.date}</td>
                <td className="px-4 py-2">{r.supervisor}</td>
                <td className="px-4 py-2 text-right mono">{r.pd ?? "—"}</td>
                <td className={`px-4 py-2 text-right mono ${r.dPd == null ? "text-slate-400" : r.dPd < 0 ? "text-red-700 font-bold" : "text-slate-600"}`}>
                  {r.dPd == null ? "—" : (r.dPd > 0 ? `+${r.dPd}` : r.dPd)}
                </td>
                <td className="px-4 py-2 text-right mono">{r.bs ?? "—"}</td>
                <td className={`px-4 py-2 text-right mono ${r.dBs == null ? "text-slate-400" : r.dBs < 0 ? "text-red-700 font-bold" : "text-slate-600"}`}>
                  {r.dBs == null ? "—" : (r.dBs > 0 ? `+${r.dBs}` : r.dBs)}
                </td>
                <td className="px-4 py-2">
                  {(r.pdMissing || r.bsMissing) ? (
                    <span className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider text-red-700 border border-red-300 rounded-sm px-1.5 py-0.5 bg-white">
                      ⚠ Discrepancy
                    </span>
                  ) : null}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

/* ===================== Incident Reports ===================== */
// "write-up" removed — disciplinary action goes through the Write-Up tab.
const INCIDENT_CATEGORIES = ["near-miss", "accident", "other"];
const EMPTY_INCIDENT = { incident_date: "", category: "near-miss", subject_user_id: "", subject_name: "", description: "", action_taken: "" };

function IncidentTab({ rows, users, reload }) {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY_INCIDENT);

  function reset() { setForm(EMPTY_INCIDENT); }

  async function save(e) {
    e.preventDefault();
    // "Subject" is now a free-text field; no user lookup.
    const payload = { ...form, subject_user_id: null };
    try {
      await api.post("/supervisor/incidents", payload);
      toast.success("Incident report saved");
      setOpen(false);
      reset();
      reload();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed");
    }
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <p className="text-xs text-slate-500">Operational incident log — near-miss, accident, or other operational event. <strong>Disciplinary action belongs on the Write-Up tab.</strong></p>
        <Dialog open={open} onOpenChange={(o) => { setOpen(o); if (!o) reset(); }}>
          <DialogTrigger asChild><NewButton data-testid="incident-new">New Incident Report</NewButton></DialogTrigger>
          <DialogContent className="max-w-2xl">
            <DialogHeader className="pb-2 border-b border-slate-200">
              <DialogTitle className="font-display text-xl">Incident Report</DialogTitle>
              <p className="text-xs text-slate-500 mt-1">Bedford Stuyvesant Volunteer Ambulance Corps</p>
            </DialogHeader>
            <form onSubmit={save} className="space-y-5 pt-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-1.5">
                  <Label className="tab-tag">Date</Label>
                  <Input type="date" required className="rounded-sm" value={form.incident_date} onChange={(e) => setForm({ ...form, incident_date: e.target.value })} data-testid="incident-date" />
                </div>
                <div className="space-y-1.5">
                  <Label className="tab-tag">Category</Label>
                  <select required className="w-full border border-slate-200 rounded-sm h-10 px-2 text-sm bg-white" value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} data-testid="incident-cat">
                    {INCIDENT_CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
                  </select>
                </div>
              </div>

              <div className="space-y-1.5">
                <Label className="tab-tag">Subject</Label>
                <Input
                  className="rounded-sm"
                  placeholder="Type a subject line for this incident"
                  value={form.subject_name}
                  onChange={(e) => setForm({ ...form, subject_name: e.target.value, subject_user_id: "" })}
                  data-testid="incident-subject"
                  required
                />
              </div>

              <div className="space-y-1.5">
                <Label className="tab-tag">Description</Label>
                <Textarea required className="rounded-sm min-h-[140px] resize-y" placeholder="What happened, where, when, and who was involved…" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} data-testid="incident-desc" />
              </div>

              <div className="space-y-1.5">
                <Label className="tab-tag">Action Taken</Label>
                <Textarea className="rounded-sm min-h-[100px] resize-y" placeholder="Immediate response, notifications made, follow-up planned…" value={form.action_taken} onChange={(e) => setForm({ ...form, action_taken: e.target.value })} data-testid="incident-action" />
              </div>

              <DialogFooter className="pt-3 border-t border-slate-200 gap-2">
                <Button type="button" variant="outline" className="rounded-sm" onClick={() => { setOpen(false); reset(); }} data-testid="incident-cancel">Cancel</Button>
                <Button type="submit" className="rounded-sm bg-slate-900 text-white" data-testid="incident-save">Save Report</Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      <div className="surface-grid">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 border-b border-slate-200">
            <tr className="text-left tab-tag text-slate-500">
              <th className="px-4 py-2">Date</th>
              <th className="px-4 py-2">Category</th>
              <th className="px-4 py-2">Subject</th>
              <th className="px-4 py-2">Author</th>
              <th className="px-4 py-2 text-right">PDF</th>
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 ? (
              <tr><td colSpan={5} className="px-4 py-6 text-slate-500">No incidents on file.</td></tr>
            ) : rows.map((r) => (
              <tr key={r.id} className="border-b border-slate-100 hover:bg-slate-50" data-testid={`incident-row-${r.id}`}>
                <td className="px-4 py-2 mono">{r.incident_date}</td>
                <td className="px-4 py-2"><Badge className="rounded-sm bg-amber-600 text-white text-[10px]">{r.category}</Badge></td>
                <td className="px-4 py-2">{r.subject_name || "—"}</td>
                <td className="px-4 py-2">{r.author_name}</td>
                <td className="px-4 py-2 text-right">
                  <a className="text-xs underline" href={`${API_BASE}/supervisor/incidents/${r.id}/pdf`} target="_blank" rel="noreferrer">
                    <DownloadSimple size={12} className="inline mr-1" />PDF
                  </a>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

/* ===================== Disciplinary Write-Ups ===================== */
const WRITEUP_CATEGORIES = ["Verbal Warning", "1st Written Warning", "2nd Written Warning", "Suspension", "Termination"];
const WRITEUP_BADGE = {
  "Verbal Warning":       "bg-yellow-500 text-white",
  "1st Written Warning":  "bg-orange-500 text-white",
  "2nd Written Warning":  "bg-orange-700 text-white",
  "Suspension":           "bg-red-600 text-white",
  "Termination":          "bg-slate-900 text-white",
};
const EMPTY_WRITEUP = { member_user_id: "", incident_date: "", category: "Verbal Warning", description: "", action_taken: "" };

function WriteUpTab({ rows, users, reload }) {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY_WRITEUP);

  function reset() { setForm(EMPTY_WRITEUP); }

  async function save(e) {
    e.preventDefault();
    if (!form.member_user_id) { toast.error("Select a member"); return; }
    try {
      await api.post("/supervisor/writeups", form);
      toast.success("Write-up filed — member notified");
      setOpen(false);
      reset();
      reload();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed");
    }
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <p className="text-xs text-slate-500">Disciplinary write-ups are saved to a permanent record. A printable PDF with signature lines is generated for the file.</p>
        <Dialog open={open} onOpenChange={(o) => { setOpen(o); if (!o) reset(); }}>
          <DialogTrigger asChild><NewButton data-testid="writeup-new">New Write-Up</NewButton></DialogTrigger>
          <DialogContent className="max-w-2xl">
            <DialogHeader className="pb-2 border-b border-slate-200">
              <DialogTitle className="font-display text-xl">Employee Disciplinary Write-Up</DialogTitle>
              <p className="text-xs text-slate-500 mt-1">Bedford Stuyvesant Volunteer Ambulance Corps</p>
            </DialogHeader>
            <form onSubmit={save} className="space-y-5 pt-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-1.5">
                  <Label className="tab-tag">Member</Label>
                  <select required className="w-full border border-slate-200 rounded-sm h-10 px-2 text-sm bg-white" value={form.member_user_id} onChange={(e) => setForm({ ...form, member_user_id: e.target.value })} data-testid="writeup-member">
                    <option value="">Select…</option>
                    {users.map((u) => <option key={u.id} value={u.id}>{u.name}</option>)}
                  </select>
                </div>
                <div className="space-y-1.5">
                  <Label className="tab-tag">Incident Date</Label>
                  <Input type="date" required className="rounded-sm" value={form.incident_date} onChange={(e) => setForm({ ...form, incident_date: e.target.value })} data-testid="writeup-date" />
                </div>
              </div>

              <div className="space-y-1.5">
                <Label className="tab-tag">Disciplinary Category</Label>
                <select required className="w-full border border-slate-200 rounded-sm h-10 px-2 text-sm bg-white" value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} data-testid="writeup-cat">
                  {WRITEUP_CATEGORIES.map((c) => <option key={c}>{c}</option>)}
                </select>
              </div>

              <div className="space-y-1.5">
                <Label className="tab-tag">Description of Incident</Label>
                <Textarea required className="rounded-sm min-h-[140px] resize-y" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} data-testid="writeup-desc" />
              </div>

              <div className="space-y-1.5">
                <Label className="tab-tag">Action Taken / Corrective Plan</Label>
                <Textarea className="rounded-sm min-h-[100px] resize-y" value={form.action_taken} onChange={(e) => setForm({ ...form, action_taken: e.target.value })} data-testid="writeup-action" />
              </div>

              <DialogFooter className="pt-3 border-t border-slate-200 gap-2">
                <Button type="button" variant="outline" className="rounded-sm" onClick={() => { setOpen(false); reset(); }} data-testid="writeup-cancel">Cancel</Button>
                <Button type="submit" className="rounded-sm bg-slate-900 text-white" data-testid="writeup-save">File Write-Up</Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      <div className="surface-grid">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 border-b border-slate-200">
            <tr className="text-left tab-tag text-slate-500">
              <th className="px-4 py-2">Date</th>
              <th className="px-4 py-2">Member</th>
              <th className="px-4 py-2">Category</th>
              <th className="px-4 py-2">Issued By</th>
              <th className="px-4 py-2 text-right">PDF</th>
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 ? (
              <tr><td colSpan={5} className="px-4 py-6 text-slate-500">No write-ups on file.</td></tr>
            ) : rows.map((r) => (
              <tr key={r.id} className="border-b border-slate-100 hover:bg-slate-50" data-testid={`writeup-row-${r.id}`}>
                <td className="px-4 py-2 mono">{r.incident_date}</td>
                <td className="px-4 py-2 font-medium">{r.member_name}</td>
                <td className="px-4 py-2"><Badge className={`rounded-sm text-[10px] ${WRITEUP_BADGE[r.category] || "bg-slate-700 text-white"}`}>{r.category}</Badge></td>
                <td className="px-4 py-2">{r.author_name}</td>
                <td className="px-4 py-2 text-right">
                  <a className="text-xs underline" href={`${API_BASE}/supervisor/writeups/${r.id}/pdf`} target="_blank" rel="noreferrer" data-testid={`writeup-pdf-${r.id}`}>
                    <DownloadSimple size={12} className="inline mr-1" />PDF
                  </a>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
